import React, { useEffect, useState, useContext } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import DatepickerField from "../helpers/DatepickerField";
import { loadConfig } from "../helpers/config";
import { AuthContext } from "../helpers/AuthContext";
import LoadingSpinner from "../components/LoadingSpinner";
import { encryptText } from "../helpers/licenseImageCrypto";
import { wrapKeyForAllRecipients } from "../helpers/licenseSharing";

function AddLicense() {
  const navigate = useNavigate();
  const { authState } = useContext(AuthContext);
  const [apiUrl, setApiUrl] = useState("");
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [hasServerKey, setHasServerKey] = useState(true);

  useEffect(() => {
    async function fetchConfig() {
      const config = await loadConfig();
      setApiUrl(config.APIURL);
      setLoadingConfig(false);
      try {
        const res = await axios.get(`${config.APIURL}/auth/public-key`, {
          headers: { accessToken: localStorage.getItem("accessToken") },
        });
        setHasServerKey(!!res.data.publicKey);
      } catch {
        setHasServerKey(false);
      }
    }
    fetchConfig();
  }, []);

  const initialValues = {
    holder_name: "",
    license_number: "",
    issue_date: "",
  };

  const validationSchema = Yup.object().shape({
    holder_name: Yup.string().required("Name des Inhabers ist erforderlich"),
    license_number: Yup.string().required("Führerscheinnummer ist erforderlich"),
    issue_date: Yup.date().required("Ausstellungsdatum ist erforderlich").typeError("Ungültiges Datum"),
  });

  const onSubmit = async (data, { setSubmitting, setStatus }) => {
    try {
      const authHeader = { accessToken: localStorage.getItem("accessToken") };
      // The license number is stored end-to-end encrypted, never in plaintext.
      const { data: created } = await axios.post(
        `${apiUrl}/licenses`,
        { holder_name: data.holder_name, issue_date: data.issue_date },
        { headers: authHeader }
      );

      const number = (data.license_number || "").trim();
      if (number) {
        try {
          const { ciphertextBase64, ivBase64, aesKey } = await encryptText(number);
          // Automatically share with every user authorized for license access.
          const keys = await wrapKeyForAllRecipients(aesKey, apiUrl, authState.id);
          await axios.post(
            `${apiUrl}/licenses/${created.id}/number`,
            { ciphertext: ciphertextBase64, iv: ivBase64, keys },
            { headers: authHeader }
          );
        } catch (encErr) {
          setStatus({
            error:
              "Führerschein angelegt, aber die Nummer konnte nicht verschlüsselt werden. " +
              "Bitte im Profil einen Verschlüsselungsschlüssel erstellen und die Nummer auf der Detailseite nachtragen.",
          });
          setSubmitting(false);
          return;
        }
      }

      navigate("/licenses");
    } catch (err) {
      setStatus({ error: err.response?.data?.error || "Fehler beim Speichern" });
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingConfig) return <LoadingSpinner text="Lade Konfiguration..." />;

  return (
    <div className="container page-offset-80">
      <div className="card">
        <div className="card-header">Neuen Führerschein hinzufügen</div>
        <div className="card-body">
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
          >
            {({ isSubmitting, status }) => (
              <Form>
                {status?.error && (
                  <div className="alert alert-error">{status.error}</div>
                )}
                {!hasServerKey && (
                  <div className="alert alert-error">
                    ⚠️ Kein Verschlüsselungsschlüssel vorhanden. Die Führerscheinnummer wird
                    Ende-zu-Ende verschlüsselt gespeichert – bitte zuerst im{" "}
                    <a href="/profile">Profil einen Schlüssel erstellen</a>.
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="holder_name">Name des Inhabers:</label>
                  <Field
                    id="holder_name"
                    name="holder_name"
                    placeholder="Max Mustermann"
                    className="form-control"
                  />
                  <ErrorMessage name="holder_name" component="span" className="error-message" />
                </div>

                <div className="form-group">
                  <label htmlFor="license_number">Führerscheinnummer:</label>
                  <Field
                    id="license_number"
                    name="license_number"
                    placeholder="B123456789"
                    className="form-control"
                  />
                  <ErrorMessage name="license_number" component="span" className="error-message" />
                  <small style={{ color: "#6c757d" }}>
                    🔒 Wird Ende-zu-Ende verschlüsselt gespeichert und ist nur mit Ihrem Private Key lesbar.
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="issue_date">Ausstellungsdatum:</label>
                  <Field
                    id="issue_date"
                    name="issue_date"
                    component={DatepickerField}
                    className="form-control"
                  />
                  <ErrorMessage name="issue_date" component="span" className="error-message" />
                </div>

                <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => navigate("/licenses")}
                  >
                    Abbrechen
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Speichert..." : "Hinzufügen"}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
}

export default AddLicense;
